// Tenantra Inc. - Bulk Operations Page
import { useState, useRef, useEffect } from 'react';
import { 
  Upload, Download, FileSpreadsheet, Home, Users, 
  CheckCircle, XCircle, AlertTriangle, Loader2,
  FileText, Info, X, FileDown, FileUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/services/api';
import { SmartImportWizard } from '@/components/dashboard/SmartImportWizard';

// Result modal
const ImportResult = ({ result, onClose }) => {
  if (!result) return null;
  const hasErrors = result.errors?.length > 0;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
        <div className={`px-4 py-3 border-b flex items-center justify-between ${hasErrors ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
          <div className="flex items-center gap-2">
            {hasErrors ? <AlertTriangle className="w-4 h-4 text-amber-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
            <div>
              <h2 className="text-sm font-semibold text-slate-800">
                {hasErrors ? 'Import Completed with Warnings' : 'Import Successful'}
              </h2>
              <p className="text-[10px] text-slate-500">{result.imported} of {result.total_rows} rows imported</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/50 rounded"><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto max-h-[50vh]">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-emerald-600">{result.imported}</div>
              <div className="text-[10px] text-slate-500">Imported</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-amber-600">{result.skipped}</div>
              <div className="text-[10px] text-slate-500">Skipped</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-slate-600">{result.total_rows}</div>
              <div className="text-[10px] text-slate-500">Total Rows</div>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-700 mb-2">Errors</h3>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {result.errors.map((err, i) => (
                  <div key={i} className="flex items-start gap-2 bg-red-50 border border-red-100 rounded p-2">
                    <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-red-700">
                      <span className="font-medium">Row {err.row}</span> — {err.field}: {err.message}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-100">
          <Button onClick={onClose} className="w-full h-8 bg-slate-800 hover:bg-slate-900 text-white text-xs border-0">Done</Button>
        </div>
      </div>
    </div>
  );
};

// File upload
const FileUploader = ({ type, onUpload, loading }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file?.name.endsWith('.csv')) setSelectedFile(file);
    else toast.error('Only CSV files accepted');
  };

  return (
    <div className="space-y-2">
      <div
        className={`border border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
          dragActive ? 'border-teal-400 bg-teal-50/50' : selectedFile ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200 hover:border-slate-300'
        }`}
        onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
        onClick={() => !selectedFile && fileInputRef.current?.click()}
      >
        <input ref={fileInputRef} type="file" accept=".csv" onChange={(e) => e.target.files?.[0] && setSelectedFile(e.target.files[0])} className="hidden" />
        {selectedFile ? (
          <div className="flex items-center justify-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-medium text-slate-700">{selectedFile.name}</span>
            <span className="text-[10px] text-slate-400">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
            <button onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }} className="p-0.5 hover:bg-slate-200 rounded">
              <X className="w-3 h-3 text-slate-400" />
            </button>
          </div>
        ) : (
          <>
            <Upload className="w-5 h-5 text-slate-300 mx-auto mb-1.5" />
            <p className="text-xs text-slate-500">Drop CSV here or <span className="text-teal-600 font-medium">browse</span></p>
          </>
        )}
      </div>
      {selectedFile && (
        <Button onClick={() => onUpload(selectedFile)} disabled={loading} className="w-full h-8 bg-slate-800 hover:bg-slate-900 text-white text-xs border-0" data-testid={`import-${type}-btn`}>
          {loading ? <><Loader2 className="w-3 h-3 animate-spin mr-1.5" /> Importing...</> : <><Upload className="w-3 h-3 mr-1.5" /> Import {type === 'properties' ? 'Properties' : 'Tenancies'}</>}
        </Button>
      )}
    </div>
  );
};

export default function BulkOperationsPage() {
  const [activeTab, setActiveTab] = useState('properties');
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [billingPlan, setBillingPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkBillingPlan(); }, []);

  const checkBillingPlan = async () => {
    try {
      const response = await api.get('/billing/current-plan');
      setBillingPlan(response.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleExport = async (type) => {
    setExporting(true);
    try {
      const response = await api.get(`/bulk/export/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tenantra_${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Export failed'); }
    finally { setExporting(false); }
  };

  const handleDownloadTemplate = async (type) => {
    try {
      const response = await api.get(`/bulk/templates/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tenantra_${type}_template.csv`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const handleImport = async (file, type) => {
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/bulk/import/${type}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(response.data);
      if (response.data.imported > 0) toast.success(`Imported ${response.data.imported} ${type}`);
    } catch (err) { toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-center">
          <div className="relative w-10 h-10 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-2 border-slate-200"></div>
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-slate-600 animate-spin"></div>
          </div>
          <p className="text-sm text-slate-500">Loading Bulk Operations</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'properties', label: 'Properties', icon: Home },
    { id: 'tenancies', label: 'Tenants', icon: Users },
  ];

  const PROP_COLS = [
    { col: 'property_type', req: 'Yes', fmt: 'apartment/house/condo/townhouse/other', ex: 'apartment' },
    { col: 'street', req: 'Yes', fmt: 'Min 5 characters', ex: '123 Main Street' },
    { col: 'unit_number', req: 'No', fmt: 'Text', ex: '101' },
    { col: 'city', req: 'Yes', fmt: 'Text', ex: 'Toronto' },
    { col: 'province', req: 'Yes', fmt: '2-letter code (ON, BC, AB...)', ex: 'ON' },
    { col: 'postal_code', req: 'Yes', fmt: 'Canadian format', ex: 'M5V 1K2' },
  ];

  const TEN_COLS = [];

  return (
    <div className="space-y-4" data-testid="bulk-operations-page">
      {/* Header */}
      <div>
        <h1 className="text-base font-semibold text-slate-800" data-testid="bulk-title">Bulk Operations</h1>
        <p className="text-xs text-slate-500 mt-0.5">Import and export your properties and tenants</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeTab === t.id ? 'border-teal-500 text-teal-700' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`} data-testid={`tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'properties' ? (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Export */}
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`export-${activeTab}`}>
          <div className="flex items-center gap-2 mb-2">
            <FileDown className="w-4 h-4 text-slate-400" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Export Properties
            </h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Download all properties as CSV. Use property IDs when importing tenants.
          </p>
          <Button onClick={() => handleExport(activeTab)} disabled={exporting}
            className="w-full h-8 text-xs bg-slate-800 hover:bg-slate-900 text-white border-0" data-testid={`export-${activeTab}-btn`}>
            {exporting ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Download className="w-3 h-3 mr-1.5" />}
            Export CSV
          </Button>
        </div>

        {/* Import */}
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`import-${activeTab}`}>
          <div className="flex items-center gap-2 mb-2">
            <FileUp className="w-4 h-4 text-slate-400" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Import Properties
            </h3>
          </div>
          <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 mb-3">
            Imported properties will require verification before adding tenants.
          </p>
          <button onClick={() => handleDownloadTemplate(activeTab)}
            className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 mb-3" data-testid={`template-${activeTab}-btn`}>
            <FileText className="w-3.5 h-3.5" /> Download CSV Template
          </button>
          <FileUploader type={activeTab} onUpload={(file) => handleImport(file, activeTab)} loading={importing} />
        </div>
      </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="export-tenancies">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <FileDown className="w-4 h-4 text-slate-400" />
                <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Export Tenants</h3>
              </div>
              <p className="text-xs text-slate-500">Download every tenant and tenancy record as a CSV backup.</p>
            </div>
            <Button onClick={() => handleExport('tenancies')} disabled={exporting}
              className="h-8 text-xs bg-slate-800 hover:bg-slate-900 text-white border-0 sm:w-40" data-testid="export-tenancies-btn">
              {exporting ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Download className="w-3 h-3 mr-1.5" />}
              Export CSV
            </Button>
          </div>
          <SmartImportWizard />
        </div>
      )}

      {/* CSV Reference (properties only) */}
      {activeTab === 'properties' && (
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
          <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">CSV Format Reference</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Column</th>
                <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Required</th>
                <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Format</th>
                <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Example</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {PROP_COLS.map((r, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-4 py-1.5 text-xs font-mono text-slate-700">{r.col}</td>
                  <td className="px-4 py-1.5 text-xs text-slate-500">{r.req}</td>
                  <td className="px-4 py-1.5 text-xs text-slate-500">{r.fmt}</td>
                  <td className="px-4 py-1.5 text-xs text-slate-400">{r.ex}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <ImportResult result={importResult} onClose={() => setImportResult(null)} />
    </div>
  );
}
